import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { useSeoHead } from "@/lib/seoHead";
import { isAgencyHost } from "@/lib/host";
import { MARKETING_LANGS, getLang, marketingLangOf, marketingPath } from "@/i18n";
import { LEGAL_KINDS } from "@/lib/legalKinds";
import { LangSync } from "@/components/LangSwitcher";
import { ImpersonationBanner, SuspendedScreen } from "@/components/Impersonation";
import { AskDialogHost } from "@/components/AskDialog";
import { LoadError, PageLoader } from "@/components/AppLoader";
import { STAFF_ROLES } from "@/lib/staff";

const Landing = lazy(() => import(/* webpackChunkName: "landing" */ "@/pages/marketing/Landing"));
const Legal = lazy(() => import("@/pages/Legal"));
const authM = () => import("@/pages/auth/AuthPages");
const Auth = Object.fromEntries(["Login", "Signup", "Callback", "Invite", "SuperAdmin", "VerifyEmail", "ForgotPassword", "ResetPassword"].map((k) => [k, lazy(() => authM().then((m) => ({ default: m[k] })))]));
const Wizard = lazy(() => import("@/pages/onboarding/Wizard"));
const Shell = lazy(() => import("@/pages/dashboard/Shell"));
const Brochure = lazy(() => import("@/pages/dashboard/Brochure"));
const OwnerReport = lazy(() => import("@/pages/dashboard/OwnerReport"));
const SiteApp = lazy(() => import(/* webpackChunkName: "site" */ "@/site/SiteApp"));
const HostSite = lazy(() => import(/* webpackChunkName: "site" */ "@/site/SiteApp").then((m) => ({ default: m.HostSite })));
const NotFound = lazy(() => import("@/pages/NotFound"));
const GuestStay = lazy(() => import("@/pages/guest/GuestStay"));

const Fallback = () => <PageLoader />;

function Protected({ children, onboarding }) {
  const { user, agency, loading, error, refresh } = useAuth();
  const loc = useLocation();
  if (loading) return <Fallback />;
  if (error && !user) return <LoadError onRetry={refresh} />;
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (STAFF_ROLES.includes(user.role)) return <Auth.SuperAdmin />;
  if (agency?.suspended && !user.impersonated_by) return <SuspendedScreen />;
  if (!onboarding && agency && !agency.onboarding_completed && user.role === "owner") return <Navigate to="/onboarding" replace />;
  return <>{children}<ImpersonationBanner /></>;
}

function AppRouter() {
  const location = useLocation();
  useEffect(() => {
    const inApp = location.pathname.startsWith("/app");
    if (!inApp) document.documentElement.classList.remove("dark");
    // The dashboard can be installed as an app (Add to Home Screen / Install). Agency websites served by the same bundle are not.
    for (const [rel, href] of [["manifest", "/manifest.json"], ["apple-touch-icon", "/icons/apple-touch-icon.png"]]) {
      const el = document.head.querySelector(`link[rel="${rel}"]`);
      if (inApp && !el) document.head.appendChild(Object.assign(document.createElement("link"), { rel, href }));
      if (!inApp && el) el.remove();
    }
    // Installable mobile web app (PWA): register the service worker only inside /app.
    if (inApp && "serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch(() => {});
    }
    // The UI language is fixed per page load; an in-app link to another language's marketing page reloads it.
    const ml = marketingLangOf(location.pathname);
    if (ml && ml !== getLang()) window.location.reload();
  }, [location.pathname]);
  if (location.hash?.includes("session_id=")) return <Suspense fallback={<Fallback />}><Auth.Callback /></Suspense>;
  return (
    <Suspense fallback={<Fallback />}>
      <Routes>
        {MARKETING_LANGS.map((l) => {
          const base = l === "en" ? "" : `/${l}`;
          return [
            <Route key={`${l}-home`} path={base || "/"} element={<Landing lang={l} />} />,
            ...LEGAL_KINDS.map((k) => <Route key={`${l}-${k}`} path={marketingPath(l, k)} element={<Legal kind={k} lang={l} />} />),
            <Route key={`${l}-pricing`} path={marketingPath(l, "pricing")} element={<PricingPage lang={l} />} />,
            l === "es" && <Route key="es-pricing-en" path="/es/pricing" element={<Navigate to="/es/precios" replace />} />,
          ];
        })}
        <Route path="/login" element={<Auth.Login />} />
        <Route path="/signup" element={<Auth.Signup />} />
        <Route path="/invite/:token" element={<Auth.Invite />} />
        <Route path="/verify-email" element={<Auth.VerifyEmail />} />
        <Route path="/forgot-password" element={<Auth.ForgotPassword />} />
        <Route path="/reset-password" element={<Auth.ResetPassword />} />
        <Route path="/onboarding" element={<Protected onboarding><Wizard /></Protected>} />
        <Route path="/print/property/:id" element={<Protected><Brochure /></Protected>} />
        <Route path="/print/owner-report/:id" element={<Protected><OwnerReport /></Protected>} />
        <Route path="/app/*" element={<Protected><Shell /></Protected>} />
        <Route path="/stay/:token" element={<GuestStay />} />
        <Route path="/s/:slug/*" element={<SiteApp />} />
        <Route path="/precios" element={<Navigate to="/es/precios" replace />} />
        <Route path="/register" element={<Navigate to="/signup" replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

// /pricing (/es/precios, /de/pricing …) is an indexable page of its own: the landing page opened at its plans.
function PricingPage({ lang }) {
  useEffect(() => {
    let n = 0;
    const id = setInterval(() => {
      const el = document.getElementById("pricing");
      if (el || ++n > 60) { clearInterval(id); el?.scrollIntoView(); }
    }, 50);
    return () => clearInterval(id);
  }, []);
  return <Landing lang={lang} />;
}

function SeoHead() {
  useSeoHead();
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <SeoHead />
      <AuthProvider>
        {isAgencyHost ? <Suspense fallback={<Fallback />}><HostSite /></Suspense> : <AppRouter />}
        {!isAgencyHost && <LangSync />}
        <Toaster position="top-center" richColors />
        <AskDialogHost />
      </AuthProvider>
    </BrowserRouter>
  );
}
